import { Injectable } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { BehaviorSubject, Observable, forkJoin, map, of, catchError } from 'rxjs';
import Highcharts from 'highcharts';
import { ApiService } from '../api/api.service';
import { FilterStateService } from '../filter-state/filter-state.service';
import { ProjectContextService } from '../project-context/project-context.service';
import { ChartDataObject } from '../../models/chart.model';
import { USER_CHART_CONFIG } from '../../models/users.model';
import { DEVICE_CHART_CONFIG, DEVICE_DETAIL_CHART_CONFIG } from '../../models/devices.model';
import { safeLocalStorageRemoveItem, safeReadJson, safeWriteJson } from '../../utils/storage';
import {
  StatisticsGranularity,
  StatisticsQueryRequest,
  StatisticsQueryResponse,
  StatisticsSeriesPoint
} from '../../models/statistics-query.model';

type TimeSeries = Array<[number, number]>;

@Injectable({
  providedIn: 'root'
})
export class ChartService {
  yearSelected = 0;
  monthSelected = 0;
  daySelected: number | null = null;

  chartObject: ChartDataObject | undefined;
  usersChartSeries: Highcharts.SeriesOptionsType[] = [];
  devicesChartSeries: Highcharts.SeriesOptionsType[] = [];
  devicesDetailChartSeries: Highcharts.SeriesOptionsType[] = [];

  private usersSerieSubject = new BehaviorSubject<string | undefined>(undefined);
  private devicesSerieSubject = new BehaviorSubject<number | undefined>(undefined);
  private yearMonthSubject = new BehaviorSubject<number[]>([]);

  private userSeriesData: Record<'registrados' | 'anonimos' | 'total', TimeSeries> = {
    registrados: [],
    anonimos: [],
    total: []
  };
  private devicesByManufacturer = new Map<string, TimeSeries>();
  private devicesByModel = new Map<string, TimeSeries>();
  private devicesTotalSeries: TimeSeries = [];

  usersSerie$ = this.usersSerieSubject.asObservable();
  devicesSerie$ = this.devicesSerieSubject.asObservable();
  yearMonth$ = this.yearMonthSubject.asObservable();

  private readonly STORAGE_KEY = 'everest.chart-selection.v1';

  constructor(
    private readonly apiService: ApiService,
    private readonly translocoSrv: TranslocoService,
    private readonly filterStateService: FilterStateService,
    private readonly projectContext: ProjectContextService
  ) {
    this.restoreSelection();
  }

  getDataByYear(year: number, month: number, day: number | null = null): ChartDataObject {
    this.yearSelected = year;
    this.monthSelected = month;
    this.daySelected = day;
    this.yearMonthSubject.next(day === null ? [year, month] : [year, month, day]);
    this.persistSelection();

    if (!this.chartObject) {
      this.chartObject = new ChartDataObject();
    }

    return this.chartObject;
  }

  resetYearData(): void {
    this.yearSelected = 0;
    this.monthSelected = 0;
    this.daySelected = null;
    this.yearMonthSubject.next([]);
    this.chartObject = undefined;
    safeLocalStorageRemoveItem(this.STORAGE_KEY);
  }

  isChartDataLoaded(): boolean {
    return this.chartObject !== undefined;
  }

  loadUsersChartData(): Observable<void> {
    const registered$ = this.requestStatistics({
      source_content_type: 'usuarios',
      target_content_type: 'usuarios',
      content_id: 'app',
      aggregation_level: 'direct',
      metric_type: 'registroUsuarios',
      metric_subtype: 'usuario_se_registra'
    });

    const anonymous$ = this.requestStatistics({
      source_content_type: 'usuarios',
      target_content_type: 'usuarios',
      content_id: 'app',
      aggregation_level: 'direct',
      metric_type: 'registroUsuarios',
      metric_subtype: 'usuario_anonimo_abre_app_por_primera_vez'
    });

    return forkJoin({ registered: registered$, anonymous: anonymous$ }).pipe(
      map(({ registered, anonymous }) => {
        const registeredMap = this.pointsToMap(registered.data[0]?.data || []);
        const anonymousMap = this.pointsToMap(anonymous.data[0]?.data || []);
        const timestamps = [...new Set([...registeredMap.keys(), ...anonymousMap.keys()])].sort((a, b) => a - b);

        this.userSeriesData.registrados = timestamps.map((timestamp) => [timestamp, registeredMap.get(timestamp) || 0]);
        this.userSeriesData.anonimos = timestamps.map((timestamp) => [timestamp, anonymousMap.get(timestamp) || 0]);
        this.userSeriesData.total = timestamps.map((timestamp) => [
          timestamp,
          (registeredMap.get(timestamp) || 0) + (anonymousMap.get(timestamp) || 0)
        ]);
      }),
      map(() => {
        this.ensureChartObject();
      }),
      catchError((error) => {
        this.userSeriesData = { registrados: [], anonimos: [], total: [] };
        console.error('Failed to load users chart data', error);
        return of(void 0);
      })
    );
  }

  loadDevicesChartData(): Observable<void> {
    const byManufacturer$ = this.requestStatistics({
      source_content_type: 'dispositivos',
      target_content_type: 'dispositivos',
      content_id: 'ALL',
      aggregation_level: 'children',
      metric_type: 'dispositivos',
      metric_subtype: 'marca'
    });

    const byModel$ = this.requestStatistics({
      source_content_type: 'dispositivos',
      target_content_type: 'dispositivos',
      content_id: 'ALL',
      aggregation_level: 'children',
      metric_type: 'dispositivos',
      metric_subtype: 'modelo'
    });

    return forkJoin({ byManufacturer: byManufacturer$, byModel: byModel$ }).pipe(
      map(({ byManufacturer, byModel }) => {
        this.devicesByManufacturer = this.seriesToMap(byManufacturer);
        this.devicesByModel = this.seriesToMap(byModel);
        this.devicesTotalSeries = this.sumAllSeries(this.devicesByManufacturer);
      }),
      map(() => {
        this.ensureChartObject();
      }),
      catchError((error) => {
        this.devicesByManufacturer.clear();
        this.devicesByModel.clear();
        this.devicesTotalSeries = [];
        console.error('Failed to load devices chart data', error);
        return of(void 0);
      })
    );
  }

  // START USERS CHART
  setUsersChartSeries(): void {
    this.usersChartSeries = [
      this.buildUsersSeries('registrados'),
      this.buildUsersSeries('anonimos'),
      this.buildUsersSeries('total')
    ].filter((series) => (series.data as TimeSeries).length > 0);
  }

  getUsersChartConfig(): Highcharts.Options {
    const monthText = this.translocoSrv.translate(`date.${Math.max(0, this.monthSelected - 1)}`).toLowerCase();

    return {
      chart: {
        type: USER_CHART_CONFIG.type
      },
      title: {
        text: this.translocoSrv.translate('stats.users.chart_title')
      },
      legend: {
        align: 'left',
        verticalAlign: 'top',
        itemStyle: {
          fontSize: '14px'
        }
      },
      yAxis: {
        title: {
          text: this.translocoSrv.translate('users.chart.label-y'),
          align: 'high'
        }
      },
      xAxis: {
        type: USER_CHART_CONFIG.xAxis.type as Highcharts.AxisTypeValue | undefined,
        title: {
          text: this.translocoSrv.translate('chart.label-x', {
            month: monthText,
            year: this.yearSelected
          }),
          align: 'high'
        },
        dateTimeLabelFormats: {
          day: USER_CHART_CONFIG.xAxis.dateTimeFormat.day
        }
      },
      series: this.usersChartSeries
    };
  }

  clickUsersSerie(index: number): void {
    this.usersSerieSubject.next(this.usersChartSeries[index].name);
  }
  // END USERS CHART

  // START DEVICES CHART
  setDevicesChartSeries(): void {
    this.devicesChartSeries = [];

    const manufacturers = [...this.devicesByManufacturer.entries()].sort((a, b) => a[0].localeCompare(b[0]));
    for (const [manufacturer, data] of manufacturers) {
      this.devicesChartSeries.push({
        name: manufacturer,
        type: 'line',
        data,
        events: {
          click: (event: any) => {
            this.clickDevicesSerie(event.point.series.index);
          }
        }
      });
    }

    if (this.devicesTotalSeries.length > 0) {
      this.devicesChartSeries.push({
        name: this.translocoSrv.translate('devices.chart.total'),
        type: 'line',
        data: this.devicesTotalSeries,
        events: {
          click: (event: any) => {
            this.clickDevicesSerie(event.point.series.index);
          }
        }
      });
    }
  }

  setDevicesDetailChartSeries(indexSerie: number): void {
    this.devicesDetailChartSeries = [];
    const selectedSeries = this.devicesChartSeries[indexSerie];
    if (!selectedSeries || this.isTotalSeries(selectedSeries.name || '')) {
      return;
    }

    const selectedManufacturer = String(selectedSeries.name);
    const byManufacturer = this.devicesByManufacturer.get(selectedManufacturer) || [];

    const candidateModels = [...this.devicesByModel.entries()].filter(([modelName]) =>
      modelName.toLowerCase().includes(selectedManufacturer.toLowerCase())
    );

    if (candidateModels.length > 0) {
      const sortedModels = candidateModels
        .sort((a, b) => this.sumSeries(b[1]) - this.sumSeries(a[1]))
        .slice(0, 10);

      for (const [modelName, data] of sortedModels) {
        this.devicesDetailChartSeries.push({
          name: modelName,
          type: 'line',
          data
        });
      }

      return;
    }

    if (byManufacturer.length > 0) {
      this.devicesDetailChartSeries.push({
        name: selectedManufacturer,
        type: 'line',
        data: byManufacturer
      });
    }
  }

  getDevicesChartConfig(): Highcharts.Options {
    const monthText = this.translocoSrv.translate(`date.${Math.max(0, this.monthSelected - 1)}`).toLowerCase();
    return {
      chart: {
        type: DEVICE_CHART_CONFIG.type
      },
      title: {
        text: this.translocoSrv.translate('devices.chart.title')
      },
      subtitle: {
        text: `<span class="subtitle">${this.translocoSrv.translate('devices.chart.subtitle')}</span>`,
        useHTML: true,
        verticalAlign: 'bottom'
      },
      legend: {
        align: 'left',
        verticalAlign: 'top',
        itemStyle: {
          fontSize: '14px'
        }
      },
      yAxis: {
        title: {
          text: this.translocoSrv.translate('devices.chart.label-y')
        }
      },
      xAxis: {
        type: DEVICE_CHART_CONFIG.xAxis.type as Highcharts.AxisTypeValue | undefined,
        title: {
          text: this.translocoSrv.translate('chart.label-x', {
            month: monthText,
            year: this.yearSelected
          }),
          align: 'high'
        },
        dateTimeLabelFormats: {
          day: DEVICE_CHART_CONFIG.xAxis.dateTimeFormat.day
        }
      },
      series: this.devicesChartSeries
    };
  }

  getDevicesDetailChartConfig(indexSerie: number): Highcharts.Options {
    const monthText = this.translocoSrv.translate(`date.${Math.max(0, this.monthSelected - 1)}`).toLowerCase();
    return {
      chart: {
        type: DEVICE_DETAIL_CHART_CONFIG.type
      },
      title: {
        text: this.devicesChartSeries[indexSerie]?.name || ''
      },
      legend: {
        align: 'left',
        verticalAlign: 'top',
        itemStyle: {
          fontSize: '14px'
        }
      },
      yAxis: {
        title: {
          text: this.translocoSrv.translate('devices.detail-chart.label-y')
        }
      },
      xAxis: {
        type: DEVICE_DETAIL_CHART_CONFIG.xAxis.type as Highcharts.AxisTypeValue | undefined,
        title: {
          text: this.translocoSrv.translate('chart.label-x', {
            month: monthText,
            year: this.yearSelected
          }),
          align: 'high'
        },
        dateTimeLabelFormats: {
          day: DEVICE_DETAIL_CHART_CONFIG.xAxis.dateTimeFormat.day
        }
      },
      series: this.devicesDetailChartSeries
    };
  }

  clickDevicesSerie(index: number): void {
    this.devicesSerieSubject.next(index);
  }

  getDevicesSerie(): number {
    return this.devicesSerieSubject.value || 0;
  }
  // END DEVICES CHART

  private buildUsersSeries(type: 'registrados' | 'anonimos' | 'total'): Highcharts.SeriesLineOptions {
    const nameKey =
      type === 'registrados' ? 'stats.users.legend.con_cuenta.label' :
      type === 'anonimos'    ? 'stats.users.legend.sin_cuenta.label' :
                               'stats.users.legend.total.label';
    return {
      name: this.translocoSrv.translate(nameKey),
      type: 'line',
      data: this.userSeriesData[type],
      events: {
        click: (event: any) => {
          this.clickUsersSerie(event.point.series.index);
        }
      }
    };
  }

  private requestStatistics(
    partial: Pick<
      StatisticsQueryRequest,
      | 'source_content_type'
      | 'target_content_type'
      | 'content_id'
      | 'aggregation_level'
      | 'metric_type'
      | 'metric_subtype'
    >
  ): Observable<StatisticsQueryResponse> {
    const selection = this.resolveSelection();
    const language = this.translocoSrv.getActiveLang();

    return this.apiService.getStatistics({
      project_id: selection.projectId,
      content_id: partial.content_id,
      source_content_type: partial.source_content_type,
      target_content_type: partial.target_content_type,
      aggregation_level: partial.aggregation_level,
      metric_type: partial.metric_type,
      metric_subtype: partial.metric_subtype,
      startDate: selection.startDate,
      endDate: selection.endDate,
      granularity: selection.granularity,
      language
    });
  }

  private resolveSelection(): {
    projectId: string;
    granularity: StatisticsGranularity;
    startDate: string;
    endDate: string;
  } {
    const snapshot = this.filterStateService.getSnapshot();
    const now = new Date();

    const year = snapshot.selectedYear ?? now.getUTCFullYear();
    const month = snapshot.selectedMonth ?? now.getUTCMonth() + 1;
    const day = snapshot.selectedDay;

    let granularity: StatisticsGranularity = 'daily';
    let start: Date;
    let end: Date;

    if (day !== null) {
      granularity = 'hourly';
      start = new Date(Date.UTC(year, month - 1, day, 0, 0, 0));
      end = new Date(Date.UTC(year, month - 1, day + 1, 0, 0, 0));
    } else {
      granularity = 'daily';
      start = new Date(Date.UTC(year, month - 1, 1, 0, 0, 0));
      end = new Date(Date.UTC(year, month, 1, 0, 0, 0));
    }

    return {
      projectId: snapshot.projectId || this.projectContext.getSelectedProjectIdSnapshot() || '',
      granularity,
      startDate: start.toISOString(),
      endDate: end.toISOString()
    };
  }

  private resolveContentName(
    names: { es: string; en?: string; ca?: string; ga?: string; va?: string },
    fallbackId: string
  ): string {
    const activeLang = this.translocoSrv.getActiveLang();
    const byLanguage = names?.[activeLang as keyof typeof names];

    return byLanguage || names?.es || names?.en || names?.ca || names?.ga || names?.va || fallbackId;
  }

  private pointsToMap(points: StatisticsSeriesPoint[]): Map<number, number> {
    const pointMap = new Map<number, number>();
    for (const point of points) {
      pointMap.set(new Date(point.dateTime).getTime(), Number(point.value || 0));
    }
    return pointMap;
  }

  private seriesToMap(response: StatisticsQueryResponse): Map<string, TimeSeries> {
    const mapData = new Map<string, TimeSeries>();
    for (const series of response.data || []) {
      const seriesName = this.resolveContentName(series.content.name, series.content.id);
      const values: TimeSeries = series.data
        .map((point) => [new Date(point.dateTime).getTime(), Number(point.value || 0)] as [number, number])
        .sort((a, b) => a[0] - b[0]);
      mapData.set(seriesName, values);
    }
    return mapData;
  }

  private sumAllSeries(seriesByName: Map<string, TimeSeries>): TimeSeries {
    const totals = new Map<number, number>();
    for (const values of seriesByName.values()) {
      for (const [timestamp, value] of values) {
        totals.set(timestamp, (totals.get(timestamp) || 0) + value);
      }
    }

    return [...totals.entries()].sort((a, b) => a[0] - b[0]).map(([timestamp, value]) => [timestamp, value]);
  }

  private sumSeries(series: TimeSeries): number {
    return series.reduce((acc, [, value]) => acc + value, 0);
  }

  private isTotalSeries(name: string): boolean {
    const normalized = name.trim().toLowerCase();
    const localizedTotal = this.translocoSrv.translate('devices.chart.total').trim().toLowerCase();
    return normalized === 'total' || normalized === localizedTotal;
  }

  private ensureChartObject(): void {
    if (!this.chartObject) {
      this.chartObject = new ChartDataObject();
    }
  }

  private restoreSelection(): void {
    const stored = safeReadJson<{ year: number; month: number; day: number | null }>(this.STORAGE_KEY);
    if (!stored) {
      return;
    }

    const year = Number(stored.year);
    const month = Number(stored.month);
    const day = stored.day === null ? null : Number(stored.day);

    if (!Number.isFinite(year) || !Number.isFinite(month) || month <= 0 || month > 12) {
      return;
    }

    this.yearSelected = year;
    this.monthSelected = month;
    this.daySelected = Number.isFinite(day as any) ? (day as number) : null;
    this.yearMonthSubject.next(this.daySelected === null ? [year, month] : [year, month, this.daySelected]);
    this.chartObject = new ChartDataObject();
  }

  private persistSelection(): void {
    if (this.yearSelected <= 0 || this.monthSelected <= 0) {
      return;
    }

    safeWriteJson(this.STORAGE_KEY, {
      year: this.yearSelected,
      month: this.monthSelected,
      day: this.daySelected
    });
  }
}
